import React, { useRef, useState, useEffect } from "react"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomButton from "components/Atoms/CustomButton"
import { PalletStateType } from "store/production.reducer"
import LabelBoxEnd from "./labels/LabelBoxEnd"
import LabelSSCC from "./labels/LabelSSCC"
import LabelPalletNo from "./labels/LabelPalletNo"
import { getTotalBoxes, getTotalKgs, getLotCode } from "./labels/labelHelpers"
import Barcode from "react-barcode"

export type LabelType = "box-end" | "sscc" | "pallet-no"

interface WidgetLabelPreviewProps {
  isOpen: boolean
  onClose: () => void
  labelType: LabelType
  pallet: PalletStateType
  production: any
}

const LABEL_TITLES: Record<LabelType, string> = {
  "box-end": "Box End Label",
  sscc: "SSCC Label",
  "pallet-no": "Pallet No Label",
}

export type SizeKey = "S" | "M" | "L"

export const LABEL_SIZES: Record<
  SizeKey,
  { width: number; height: number; label: string }
> = {
  S: { width: 100, height: 75, label: "S (100x75 mm)" },
  M: { width: 100, height: 100, label: "M (100x100 mm)" },
  L: { width: 100, height: 150, label: "L (100x150 mm)" },
}

const getDefaultSize = (type: LabelType): SizeKey => {
  if (type === "sscc") return "L"
  if (type === "pallet-no") return "S"
  if (type === "box-end") return "S"
  return "S"
}

export default function WidgetLabelPreview({
  isOpen,
  onClose,
  labelType,
  pallet,
  production,
}: WidgetLabelPreviewProps) {
  const printRef = useRef<HTMLDivElement>(null)
  const [selectedSize, setSelectedSize] = useState<SizeKey>(
    getDefaultSize(labelType)
  )
  const [isRotated, setIsRotated] = useState<boolean>(false)
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false)

  useEffect(() => {
    setSelectedSize(getDefaultSize(labelType))
    setIsDuplicate(true)
  }, [labelType])

  const sizeConfig = LABEL_SIZES[selectedSize]

  const handlePrint = () => {
    if (!printRef.current) return

    const printWindow = window.open("", "_blank")
    if (!printWindow) return

    const content = printRef.current.innerHTML
    const palletNo = pallet?.palletNumber ?? "label"
    const title = LABEL_TITLES[labelType]

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title} - Pallet ${palletNo}</title>
          <style>
            * { 
              box-sizing: border-box; 
              margin: 0; 
              padding: 0; 
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: flex-start;
              padding: 20px;
              background: #fff;
            }
            .print-wrapper { 
              width: ${sizeConfig.width}mm; 
              height: ${sizeConfig.height}mm;
              overflow: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #fff;
            }
            table { border-collapse: collapse; }
            @media print {
              body { padding: 0; }
              @page { 
                size: ${sizeConfig.width}mm ${sizeConfig.height}mm;
                margin: 0; 
              }
            }
          </style>
        </head>
        <body>
          <div class="print-wrapper">${content}</div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  const renderLabel = () => {
    switch (labelType) {
      case "box-end":
        return (
          <LabelBoxEnd
            pallet={pallet}
            production={production}
            size={selectedSize}
          />
        )
      case "sscc":
        return <LabelSSCC pallet={pallet} production={production} />
      case "pallet-no":
        if (isDuplicate) {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                height: "100%",
                gap: "3mm",
              }}
            >
              {[0, 1].map(i => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  <LabelPalletNo
                    pallet={pallet}
                    production={production}
                    compact
                  />
                </div>
              ))}
            </div>
          )
        }
        return <LabelPalletNo pallet={pallet} production={production} />
    }
  }

  return (
    <CustomDialog
      light
      width="45%"
      icon="print"
      title={`${LABEL_TITLES[labelType]} — Pallet ${
        pallet?.palletNumber ?? ""
      }`}
      isOpen={isOpen}
      onClose={onClose}
      footerShowCloseButton={false}
      footerRightButton={
        <div className="flex items-center justify-end w-full gap-2">
          <CustomButton text="Close" onClick={onClose} />
          <CustomButton
            text="Rotate Layout"
            onClick={() => setIsRotated(!isRotated)}
          />
          <CustomButton
            intent="primary"
            icon="print"
            text="Print / Download"
            onClick={handlePrint}
          />
        </div>
      }
    >
      <div className=" flex flex-col gap-2 bg-gray-50/50 rounded-b-lg">
        {/* Controls Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm gap-3">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-800">
              Thermal Print Preview
            </span>
            <span className="text-xs text-gray-500">
              Labels will print in black & white
            </span>
          </div>
          <div className="flex items-center gap-3">
            {labelType === "pallet-no" && (
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDuplicate}
                  onChange={e => setIsDuplicate(e.target.checked)}
                />
                2 labels on 1 sticker
              </label>
            )}
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Print Size:
            </span>
            <select
              value={selectedSize}
              onChange={e => setSelectedSize(e.target.value as SizeKey)}
              className="border border-gray-300 rounded-md py-1.5 px-3 text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium shadow-sm cursor-pointer hover:border-gray-400 transition-colors w-full sm:w-auto"
            >
              {Object.entries(LABEL_SIZES).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Label preview */}
        <div className="bg-gray-200/70 border border-gray-300 rounded-lg flex justify-center items-center overflow-auto min-h-[400px] sm:min-h-[400px] shadow-inner">
          {/* Wrapper to enforce visual preview size and grayscale */}
          <div
            className="grayscale shadow-md relative transition-all duration-300"
            style={{
              width: `${sizeConfig.width}mm`,
              height: `${sizeConfig.height}mm`,
              overflow: "hidden",
            }}
          >
            {/* The actual ref content for printing */}
            <div
              ref={printRef}
              className="w-full h-full bg-white flex justify-center items-center overflow-hidden"
            >
              <div
                style={{
                  width: isRotated
                    ? `${sizeConfig.height}mm`
                    : `${sizeConfig.width}mm`,
                  height: isRotated
                    ? `${sizeConfig.width}mm`
                    : `${sizeConfig.height}mm`,
                  transform: isRotated ? "rotate(-90deg)" : "none",
                  transformOrigin: "center center",
                  transition: "all 0.3s ease",
                  padding: "4px",
                }}
                className="flex-shrink-0"
              >
                {renderLabel()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CustomDialog>
  )
}
